'use client';

import { BASE_URL } from 'shared/src/types/region.type';

/**
 * WebSite Schema Component
 * Provides structured data for the Inception website entity (Google reads
 * name/alternateName for the SERP site name — "Inception" is now canonical,
 * the InceptionAI variants stay as aliases to keep the branded keyword)
 * Optimized for 2026 GEO (Generative Engine Optimization) best practices
 */
export const WebSiteSchema = () => {
  const baseURL = BASE_URL.CA; // Primary domain
  const organizationId = `${baseURL}#organization`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${baseURL}#website`,
    name: 'Inception', // Canonical brand name
    alternateName: [
      'InceptionAI', // Former canonical / branded keyword (CamelCase)
      'Inception AI', // Common search: space + caps
      'inception ai', // Common search: lowercase with space (target keyword)
      'InceptionAi', // Legacy site casing
      'Inception Ai', // Mixed case
      'inception ia', // Typo/French variation
      'inceptionai', // No space lowercase
    ],
    url: baseURL,
    description: `The best product for you is already out there. Our AI finds it, because we answer to you, not advertisers. Inception, also known as InceptionAI, gives you AI-researched product recommendations for your region, with no sponsored placements.`,
    publisher: {
      '@type': 'Organization',
      '@id': organizationId,
    },
    potentialAction: [
      {
        '@type': 'SearchAction',
        name: 'Inception Human Search',
        target: {
          '@type': 'EntryPoint',
          // `autoSearch=true`: the search page runs the words for a human tab
          // (behind the bar's client gate); a plain `?q=` only prefills them.
          urlTemplate: `${baseURL}/?q={search_term_string}&autoSearch=true`,
        },
        'query-input': 'required name=search_term_string',
      },
      {
        '@type': 'SearchAction',
        name: 'Inception AI Search Tool',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${baseURL}/api/ai-search?q={search_term_string}&locale={locale}&searchType={search_type}`,
          httpMethod: 'GET',
          contentType: 'application/json',
          actionPlatform: [
            'https://schema.org/HTTPRequest',
          ],
        },
        'query-input': [
          'required name=search_term_string',
          'required name=locale',
          'name=search_type valueRequired=false (product|category|kit|software, defaults to category)',
        ],
      },
    ],
    sameAs: [
      'https://www.linkedin.com/company/inceptionai',
      'https://github.com/inceptionai',
      'https://www.facebook.com/InceptionAI',
      'https://www.instagram.com/inceptionai_inc',
      'https://www.youtube.com/@InceptionAI',
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schema),
      }}
    />
  );
};
